import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, CheckCircle, XCircle, MessageSquare } from "lucide-react";
import useAuth from "../../contexts/AuthContext";
import Loader from "../../common_components/Loader";
import { useToast } from "../../common_components/Toast";
import DynamicSection from "../dynamic-form/components/DynamicSection";
import { ApplicationStatus, PaymentStatus } from "../../types/status";

interface FormSection {
  id: string;
  title?: string;
  fields: unknown[];
}

interface ApplicationSchema {
  sections?: FormSection[];
}

interface Recruitment {
  title: string;
  applicationFormSchema?: ApplicationSchema;
}

interface Payment {
  amount?: number;
  utrNumber?: string;
  paymentDate?: string;
  status?: string;
}

interface Application {
  formData: Record<string, unknown>;
  payment?: Payment;
  declaration?: { acceptedAt?: string };
  status: string;
  reviewComments?: string;
  submittedAt?: string;
}

export default function ReviewApplication() {
  const { recruitmentId, applicationId } = useParams<{
    recruitmentId: string;
    applicationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const toast = useToast();

  const isNonFaculty = location.pathname.includes("non-faculty");
  const apiPrefix = isNonFaculty ? "/non-faculty" : "/faculty";
  const redirectUrl = isNonFaculty
    ? `/non-faculty/admin/${recruitmentId}/applications/all`
    : `/faculty/admin/${recruitmentId}/applications/all`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [application, setApplication] = useState<Application | null>(null);
  const [recruitment, setRecruitment] = useState<Recruitment | null>(null);

  // Review status states
  const [reviewStatus, setReviewStatus] = useState(ApplicationStatus.SUBMITTED);
  const [paymentStatus, setPaymentStatus] = useState(PaymentStatus.PENDING);
  const [reviewComments, setReviewComments] = useState("");

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [appRes, recRes] = await Promise.all([
          apiRequest(`${apiPrefix}/application/${applicationId}`, { method: "GET" }),
          apiRequest(`${apiPrefix}/recruitment/${recruitmentId}`, { method: "GET" }),
        ]);

        if (!appRes?.application) throw new Error("Application not found");
        if (!recRes?.recruitment) throw new Error("Recruitment not found");

        setApplication(appRes.application);
        setRecruitment(recRes.recruitment);
        setReviewStatus(appRes.application.status || ApplicationStatus.SUBMITTED);
        setPaymentStatus(appRes.application.payment?.status || PaymentStatus.PENDING);
        setReviewComments(appRes.application.reviewComments || "");
      } catch (err: unknown) {
        console.error("Load review error:", err);
        const e = err as { message?: string };
        setError(e.message || "Failed to load application data.");
      } finally {
        setLoading(false);
      }
    }

    if (applicationId && recruitmentId) {
      loadData();
    }
  }, [apiPrefix, apiRequest, applicationId, recruitmentId]);

  const handleSaveReview = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`${apiPrefix}/application/admin/${applicationId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: reviewStatus,
          paymentStatus: paymentStatus,
          reviewComments,
        }),
      });

      toast.success("Review and status updated successfully!");
      navigate(redirectUrl);
    } catch (err: unknown) {
      console.error("Save review failed:", err);
      const e = err as { message?: string };
      toast.error(e.message || "Failed to update review status.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl border p-8 max-w-md shadow-md text-center space-y-4">
          <XCircle className="w-16 h-16 text-red-600 mx-auto" />
          <h2 className="text-xl font-bold">Error Loading Details</h2>
          <p className="text-gray-600">{error}</p>
          <button onClick={() => navigate(-1)} className="w-full py-2 bg-slate-800 text-white rounded-lg">
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!application || !recruitment) return null;

  const { formData, payment } = application;
  const sections = recruitment.applicationFormSchema?.sections || [];

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between no-print">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm font-semibold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <a
            href={`${apiPrefix}/application/${recruitmentId}/${applicationId}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 text-white hover:bg-slate-900 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            <Printer className="w-4 h-4" /> Print Preview
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Main Form Fields */}
          <div className="lg:col-span-2 space-y-6">
            {applicationId && sections.map((section) => (
              <DynamicSection
                key={section.id}
                section={section}
                sectionData={(formData[section.id] as Record<string, unknown>) || {}}
                onChange={() => { }}
                errors={{}}
                applicationId={applicationId}
                disabled={true}
                recruitment={recruitment}
              />
            ))}
          </div>

          {/* Admin Action Pane */}
          <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
              <div className="border-b pb-3">
                <h3 className="text-lg font-bold text-gray-900">Admin Review Panel</h3>
                <p className="text-xs text-gray-500 mt-1">Review application details, documents, and payments.</p>
              </div>

              {/* Payment Verification Card */}
              {payment?.amount && payment.amount > 0 ? (
                <div className="p-4 bg-slate-50 border border-gray-200 rounded-xl space-y-4">
                  <span className="block text-sm font-bold text-slate-800">Registration Fee Verification</span>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500">Amount Due</p>
                      <p className="font-bold text-gray-900">₹{payment.amount}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">UTR / Ref ID</p>
                      <p className="font-bold font-mono text-gray-900">{payment.utrNumber || "N/A"}</p>
                    </div>
                    <div className="col-span-2 pt-1 border-t">
                      <p className="text-gray-500">Payment Date</p>
                      <p className="font-bold text-gray-900">
                        {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString("en-IN") : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-bold text-gray-500">Payment Verification</label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value as any)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs"
                    >
                      <option value={PaymentStatus.PENDING}>PENDING</option>
                      <option value={PaymentStatus.PAID}>SUCCESSFUL (Approved)</option>
                      <option value={PaymentStatus.VERIFIED}>VERIFIED</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-xs font-semibold">
                  Zero Application Fee applicable for this category.
                </div>
              )}

              {/* Status Decision Card */}
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="block text-sm font-bold text-gray-700">Application Decision</label>
                  <select
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value as any)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={ApplicationStatus.SUBMITTED}>SUBMITTED (In Review)</option>
                    <option value={ApplicationStatus.ACCEPTED}>ACCEPTED (Shortlisted)</option>
                    <option value={ApplicationStatus.REJECTED}>REJECTED</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-bold text-gray-700 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-gray-400" /> Comments
                  </label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={4}
                    placeholder="Add comments or rejection reasons"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Save Button */}
              <button
                type="button"
                onClick={handleSaveReview}
                disabled={saving}
                className="w-full flex items-center justify-center gap-1.5 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all disabled:opacity-60"
              >
                <CheckCircle className="w-4 h-4" /> Submit Review Decision
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
